import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Skeleton,
  Grid,
  Card,
  CardContent,
  Divider,
  Tooltip,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CloudUpload as DeployIcon,
  Devices as DevicesIcon,
} from '@mui/icons-material';
import { api, endpoints } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useMode } from '../contexts/ModeContext';
import { useAuth } from '../contexts/AuthContext';

interface Configuration {
  id: string;
  name: string;
  description: string;
  config_type: string;
  target_devices: string[];
  is_active: boolean;
  created_at: string;
  current_version?: {
    version_number: string;
    content: any;
  };
}

interface Device {
  id: string;
  device_id: string;
  name: string;
  device_type: string;
  status: string;
}

interface ConfigurationFormData {
  name: string;
  description: string;
  config_type: string;
  target_devices: string[];
  version_number: string;
  content: any;
  change_log: string;
}

const Configurations: React.FC = () => {
  const navigate = useNavigate();
  const { mode } = useMode();
  const { effectiveUser: user } = useAuth();
  const capabilities = Array.isArray((user as any)?.capabilities) ? (user as any).capabilities : [];
  const canReadDeployments = capabilities.includes('deployments:read');
  const canWriteDeployments = capabilities.includes('deployments:write');
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [deployingConfig, setDeployingConfig] = useState<Configuration | null>(null);
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [deploying, setDeploying] = useState(false);
  const [editingConfig, setEditingConfig] = useState<Configuration | null>(null);
  const [formData, setFormData] = useState<ConfigurationFormData>({
    name: '',
    description: '',
    config_type: 'roi',
    target_devices: [],
    version_number: '1.0.0',
    content: {},
    change_log: '',
  });

  const configTypes = [
    { value: 'roi', label: 'ROI Settings', icon: '🎯' },
    { value: 'pipeline', label: 'Pipeline Config', icon: '⚙️' },
    { value: 'mission', label: 'Mission Config', icon: '🚀' },
    { value: 'global', label: 'Global Settings', icon: '🌐' },
  ];

  useEffect(() => {
    fetchConfigurations();
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchConfigurations/fetchDevices are stable module-level functions
  }, []);

  const fetchConfigurations = async () => {
    try {
      setLoading(true);
      const module_scope = mode === 'ocr' ? 'ocr' : 'warehouse';
      const response = await api.get(endpoints.configurations.list, { params: { module_scope } });
      setConfigurations(response.data.configurations || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch configurations');
    } finally {
      setLoading(false);
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await api.get(endpoints.devices.list);
      setDevices(response.data.devices || []);
    } catch (err: any) {
      console.error('Failed to fetch devices:', err);
    }
  };

  const handleOpenDialog = (config?: Configuration) => {
    if (config) {
      setEditingConfig(config);
      setFormData({
        name: config.name,
        description: config.description || '',
        config_type: config.config_type,
        target_devices: config.target_devices || [],
        version_number: config.current_version?.version_number || '1.0.0',
        content: config.current_version?.content || {},
        change_log: '',
      });
    } else {
      setEditingConfig(null);
      setFormData({
        name: '',
        description: '',
        config_type: 'roi',
        target_devices: [],
        version_number: '1.0.0',
        content: {},
        change_log: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingConfig(null);
    setFormData({
      name: '',
      description: '',
      config_type: 'roi',
      target_devices: [],
      version_number: '1.0.0',
      content: {},
      change_log: '',
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingConfig) {
        // Update existing configuration
        await api.put(endpoints.configurations.update(editingConfig.id), formData);
      } else {
        // Create new configuration
        const module_scope = mode === 'ocr' ? 'ocr' : 'warehouse';
        await api.post(endpoints.configurations.create, { ...(formData as any), module_scope });
      }
      handleCloseDialog();
      fetchConfigurations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save configuration');
    }
  };

  const handleDelete = async (configId: string) => {
    if (window.confirm('Are you sure you want to delete this configuration?')) {
      try {
        await api.delete(endpoints.configurations.delete(configId));
        fetchConfigurations();
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to delete configuration');
      }
    }
  };

  // Deploy functionality
  const handleOpenDeployDialog = (config: Configuration) => {
    setDeployingConfig(config);
    setSelectedDevices([]);
    setDeployDialogOpen(true);
  };

  const handleCloseDeployDialog = () => {
    setDeployDialogOpen(false);
    setDeployingConfig(null);
    setSelectedDevices([]);
  };

  const handleToggleDevice = (deviceId: string) => {
    setSelectedDevices(prev =>
      prev.includes(deviceId)
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleSelectAllDevices = () => {
    if (selectedDevices.length === devices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(devices.map(d => d.id));
    }
  };

  const handleDeploy = async () => {
    if (!deployingConfig || selectedDevices.length === 0) return;

    setDeploying(true);
    try {
      await api.post(endpoints.deployments.create, {
        configuration_id: deployingConfig.id,
        target_devices: selectedDevices,
        deployment_type: 'immediate',
        notes: `Deployment of ${deployingConfig.name}`,
      });
      
      setSuccess(`Deployment created! Deploying "${deployingConfig.name}" to ${selectedDevices.length} device(s).`);
      handleCloseDeployDialog();
      
      // Optionally navigate to deployments page
      setTimeout(() => {
        navigate('/deployments');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create deployment');
    } finally {
      setDeploying(false);
    }
  };

  const getConfigTypeIcon = (type: string) => {
    const configType = configTypes.find(t => t.value === type);
    return configType ? configType.icon : '⚙️';
  };

  const getConfigTypeLabel = (type: string) => {
    const configType = configTypes.find(t => t.value === type);
    return configType ? configType.label : type;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Configurations
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={!canWriteDeployments}
        >
          New Configuration
        </Button>
      </Box>

      {canReadDeployments && !canWriteDeployments && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read-only access to configurations. Write and deploy actions are disabled.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Grid container spacing={3}>
        {/* Configuration Statistics */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📊 Configuration Overview
              </Typography>
              <Typography variant="h3" color="primary">
                {configurations.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Configurations
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                ✅ Active Configurations
              </Typography>
              <Typography variant="h3" color="success.main">
                {configurations.filter(c => c.is_active).length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Currently Active
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🎯 ROI Configurations
              </Typography>
              <Typography variant="h3" color="info.main">
                {configurations.filter(c => c.config_type === 'roi').length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                ROI Settings
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Configurations Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && configurations.length === 0 ? (
                [0, 1, 2].map((i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton variant="text" width={140} /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} /></TableCell>
                    <TableCell><Skeleton variant="text" width={100} /></TableCell>
                    <TableCell><Skeleton variant="text" width={60} /></TableCell>
                    <TableCell><Skeleton variant="text" width={100} /></TableCell>
                    <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                  </TableRow>
                ))
              ) : null}
              {configurations.map((config) => (
                <TableRow key={config.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <span style={{ marginRight: '8px' }}>
                        {getConfigTypeIcon(config.config_type)}
                      </span>
                      <Typography variant="subtitle2">{config.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getConfigTypeLabel(config.config_type)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {config.description || 'No description'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={config.is_active ? 'Active' : 'Inactive'}
                      color={config.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {config.current_version?.version_number || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(config.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit Configuration" arrow>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(config)}
                        color="primary"
                        disabled={!canWriteDeployments}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Configuration" arrow>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(config.id)}
                        color="error"
                        disabled={!canWriteDeployments}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View Details" arrow>
                      <IconButton size="small" color="info">
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deploy to Devices" arrow>
                      <IconButton 
                        size="small" 
                        color="success"
                        onClick={() => handleOpenDeployDialog(config)}
                        disabled={!canWriteDeployments}
                      >
                        <DeployIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Configuration Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingConfig ? 'Edit Configuration' : 'New Configuration'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Configuration Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Configuration Type</InputLabel>
                <Select
                  value={formData.config_type}
                  onChange={(e) => setFormData({ ...formData, config_type: e.target.value })}
                  label="Configuration Type"
                >
                  {configTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Version Number"
                value={formData.version_number}
                onChange={(e) => setFormData({ ...formData, version_number: e.target.value })}
                placeholder="1.0.0"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Change Log"
                value={formData.change_log}
                onChange={(e) => setFormData({ ...formData, change_log: e.target.value })}
                placeholder="Description of changes"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Configuration Content (JSON)"
                value={JSON.stringify(formData.content, null, 2)}
                onChange={(e) => {
                  try {
                    const content = JSON.parse(e.target.value);
                    setFormData({ ...formData, content });
                  } catch (err) {
                    // Invalid JSON, keep as string
                  }
                }}
                multiline
                rows={6}
                placeholder='{"example": "configuration"}'
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!canWriteDeployments}>
            {editingConfig ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deploy Dialog */}
      <Dialog open={deployDialogOpen} onClose={handleCloseDeployDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DeployIcon color="success" />
            Deploy Configuration
          </Box>
        </DialogTitle>
        <DialogContent>
          {deployingConfig && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Deploying: <strong>{deployingConfig.name}</strong>
              </Alert>
              
              <Typography variant="subtitle1" gutterBottom>
                Select Target Devices ({selectedDevices.length} selected)
              </Typography>
              
              {devices.length === 0 ? (
                <Alert severity="warning">
                  No devices available. Please add devices first.
                </Alert>
              ) : (
                <>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Checkbox
                      checked={selectedDevices.length === devices.length}
                      indeterminate={selectedDevices.length > 0 && selectedDevices.length < devices.length}
                      onChange={handleSelectAllDevices}
                    />
                    <Typography variant="body2">Select All</Typography>
                  </Box>
                  <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                    <List dense>
                      {devices.map((device) => (
                        <ListItem
                          key={device.id}
                          button
                          onClick={() => handleToggleDevice(device.id)}
                        >
                          <ListItemIcon>
                            <Checkbox
                              checked={selectedDevices.includes(device.id)}
                              tabIndex={-1}
                              disableRipple
                            />
                          </ListItemIcon>
                          <ListItemIcon>
                            <DevicesIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={device.name}
                            secondary={`${device.device_type} • ${device.device_id}`}
                          />
                          <Chip
                            label={device.status}
                            size="small"
                            color={device.status === 'online' ? 'success' : 'default'}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeployDialog}>Cancel</Button>
          <Button
            onClick={handleDeploy}
            variant="contained"
            color="success"
            disabled={!canWriteDeployments || selectedDevices.length === 0 || deploying}
            startIcon={deploying ? <CircularProgress size={20} /> : <DeployIcon />}
          >
            {deploying ? 'Deploying...' : `Deploy to ${selectedDevices.length} Device(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Configurations;
